import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActorGroupMember, DRILL_MEMBERS_SHOWN, MemberConnections } from './graphTypes';
import { MemberConnectionsLine } from './MemberConnectionsLine';
import { GraphHostActions } from './hostActions';
import { platformNamesMapping } from '../../utils/platforms';
import { ReactComponent as AddToIcon } from '../../assets/addTo.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { FALLBACK_PLATFORM_ICON, PLATFORM_ICONS } from './platformIcons';
import styles from './SocialGraphPOC.module.scss';

// Display-ready data for the right-hand group panel; the page resolves the
// active GraphHighlight into this shape (see highlightGroup memo)
export interface HighlightGroupInfo {
  // entity type shown in the titlebar: Category / Community / Z-Score anomaly
  title: string;
  // the entity itself, e.g. "Amplifier" or "Community #1"
  name: string;
  // role/bucket/flag explanation; communities have none while density is hidden
  description?: string;
  members: (ActorGroupMember & { z?: number })[];
  // z-score bucket panels show each member's avg z
  showZ: boolean;
}

// Right panel for a selected category / community / z-score band (Figma
// 710-8554 / 710-8785 / 717-22805). Same shell classes as the Actor panel —
// selecting a member replaces this panel with the Actor panel.
export const GroupPanel: React.FC<{
  group: HighlightGroupInfo;
  // every actor's connections split by direction — each member row shows its
  // own under the name (PM 2026-10-16: see how active each actor is without
  // opening them one by one); same lines for every group kind
  connectionsById: Map<string, MemberConnections>;
  onSelectActor: (nodeId: string) => void;
  onClose: () => void;
  actions: GraphHostActions | null;
}> = ({ group, connectionsById, onSelectActor, onClose, actions }) => {
  const { t } = useTranslation();
  const shownMembers = group.members.slice(0, DRILL_MEMBERS_SHOWN);

  return (
    <aside className={styles.nodePanel} aria-label={`${group.title}: ${group.name}`}>
      <div className={styles.nodePanelHeader}>
        <h2 className={styles.nodePanelTitle}>{group.title}</h2>
        <button
          type="button"
          className={styles.nodePanelClose}
          onClick={onClose}
          aria-label={t('Close panel')}
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </div>
      <div className={styles.nodePanelHead}>
        <p className={styles.nodePanelName}>{group.name}</p>
        {group.description && <p className={styles.groupPanelDesc}>{group.description}</p>}
        {actions && (
          <div className={styles.nodePanelControls}>
            <button
              type="button"
              className={styles.nodePanelControlBtn}
              onClick={() => actions.addToGroup(group.members.map((member) => member.id))}
            >
              <AddToIcon aria-hidden="true" />
              {t('Add to group')}
            </button>
          </div>
        )}
      </div>
      <div className={styles.nodePanelBody}>
        <div className={styles.nodePanelSection}>
          <div className={styles.connectionsGroupHead}>
            <span className={styles.nodePanelSectionTitle}>{t('Actors')}</span>
            <span className={styles.connectionsGroupCount}>{group.members.length}</span>
          </div>
          <ul className={styles.connectionsList}>
            {shownMembers.map((member) => {
              const MemberPlatformIcon = PLATFORM_ICONS[member.platform] ?? FALLBACK_PLATFORM_ICON;
              const connections = connectionsById.get(member.id);
              return (
                // the + button can't nest inside the row button, so the li is
                // the flex row holding both siblings (same as the Connections tab)
                <li key={member.id} className={styles.connectionRowWrap}>
                  <button
                    type="button"
                    className={styles.connectionRow}
                    onClick={() => onSelectActor(member.id)}
                  >
                    <span
                      className={styles.connectionRowIcon}
                      role="img"
                      aria-label={platformNamesMapping[member.platform] ?? member.platform}
                    >
                      <MemberPlatformIcon aria-hidden="true" />
                    </span>
                    <span className={styles.connectionRowText}>
                      <span className={styles.connectionRowName}>{member.label}</span>
                      {connections && <MemberConnectionsLine connections={connections} />}
                    </span>
                    {group.showZ && member.z !== undefined && (
                      <span className={styles.groupMemberZ}>{member.z.toFixed(2)}</span>
                    )}
                  </button>
                  {/* per-actor "add to group", as on the Connections tab rows; the
                      head button above still adds the whole group */}
                  {actions && (
                    <button
                      type="button"
                      className={styles.connectionRowAdd}
                      onClick={() => actions.addToGroup([member.id])}
                      aria-label={t('Add {{name}} to group', { name: member.label })}
                    >
                      <AddToIcon aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {group.members.length > DRILL_MEMBERS_SHOWN && (
            <p className={styles.overviewNote}>
              {t('+{{count}} more actors', { count: group.members.length - DRILL_MEMBERS_SHOWN })}
            </p>
          )}
        </div>
      </div>
    </aside>
  );
};
